import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui';
import { type ImportBatch, registrationImportService } from '../services/registrationImportService';

export function RegistrantImportPanel({ eventId, onConfirmed }: { eventId: string; onConfirmed?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    registrationImportService.latest(eventId).then((value) => { if (active && value) setBatch(value); }).catch(() => undefined);
    return () => { active = false; };
  }, [eventId]);

  async function review() {
    if (!file) return;
    setBusy(true);
    try {
      const reviewed = await registrationImportService.preview(eventId, file);
      const resolved = reviewed.state === 'confirmed' ? await registrationImportService.confirm(eventId, reviewed.id) : reviewed;
      setBatch(resolved);
      if (resolved.state === 'confirmed') onConfirmed?.();
    }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Unable to review workbook'); }
    finally { setBusy(false); }
  }
  async function confirm() {
    if (!batch) return;
    setBusy(true);
    try { setBatch(await registrationImportService.confirm(eventId, batch.id)); onConfirmed?.(); toast.success('Import confirmed. No email has been sent.'); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Unable to confirm import'); }
    finally { setBusy(false); }
  }
  async function activate() {
    if (!batch) return;
    setBusy(true);
    try { await registrationImportService.activate(eventId, batch.id); toast.success('Activation emails queued.'); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Unable to send activation emails'); }
    finally { setBusy(false); }
  }
  const newAccounts = batch?.summary.new_accounts ?? 0;
  return <section className="mt-8 rounded-xl border border-gray-800 bg-gray-900 p-5">
    <h2 className="text-base text-white">Import registered attendees</h2>
    <p className="mt-1 text-sm text-gray-400">Review the workbook first. Confirming creates memberships only; activation emails require the separate send action.</p>
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <label className="text-sm text-gray-300">Registration workbook<input aria-label="Registration workbook" className="ml-3 text-xs" type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
      <Button onClick={review} disabled={!file} loading={busy}>Review import</Button>
    </div>
    {batch && <div className="mt-4 rounded-lg bg-gray-800/70 p-4 text-sm">
      <p className="text-gray-200"><strong>{newAccounts} new accounts</strong> · {batch.summary.existing_accounts ?? 0} existing accounts · {batch.summary.existing_memberships ?? 0} already registered · {batch.summary.invalid_rows ?? 0} invalid rows</p>
      {batch.rows.filter((row) => row.status === 'invalid').map((row) => <p key={row.row_number} className="mt-2 text-amber-300">Row {row.row_number}: {row.error_codes.join(', ')}</p>)}
      {batch.state === 'validated' ? <Button className="mt-4" onClick={confirm} loading={busy} disabled={(batch.summary.invalid_rows ?? 0) > 0}>Confirm import</Button> : <Button className="mt-4" variant="danger" onClick={activate} loading={busy}>Send activation emails ({newAccounts})</Button>}
    </div>}
  </section>;
}
